import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

interface EditableValueProps {
  value: unknown;
  /**
   * Called when the user blurs the input AFTER changing the value. Returning a
   * Promise lets the parent disable the field while the (stub) PATCH resolves.
   */
  onCommit: (nextValue: string) => void | Promise<void>;
  /** Visual cue for missing required fields — red border + placeholder. */
  missing?: boolean;
  /** When true, blocks further edits (e.g. while a save is in flight). */
  disabled?: boolean;
  /** Optional id used to associate with a `<label>` upstream. */
  inputId?: string;
}

/**
 * Click-to-edit inline value.
 *
 * Rules:
 *  - Click on the display surface → switches to an `<input>`.
 *  - `Esc` cancels.
 *  - Blur with an unchanged value cancels silently.
 *  - Blur with a changed value calls `onCommit` and keeps the input mounted
 *    until the parent's promise resolves.
 */
export function EditableValue({
  value,
  onCommit,
  missing = false,
  disabled = false,
  inputId,
}: EditableValueProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(toDisplay(value));
  const initialRef = useRef<string>(toDisplay(value));
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Keep draft in sync if the parent overwrites the value (e.g. revalidation refetch).
  useEffect(() => {
    if (!editing) {
      const next = toDisplay(value);
      setDraft(next);
      initialRef.current = next;
    }
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  if (!editing) {
    const display = toDisplay(value);
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) {
            return;
          }
          initialRef.current = display;
          setDraft(display);
          setEditing(true);
        }}
        className={clsx(
          'flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors',
          missing
            ? 'border-rose-400 bg-rose-50 text-rose-700'
            : 'border-slate-200 bg-white text-slate-900 hover:border-primary/40 hover:bg-slate-50',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <span className="truncate">
          {missing && display.length === 0 ? (
            <span className="italic text-rose-500">No encontrado — añade el valor</span>
          ) : display.length === 0 ? (
            <span className="italic text-slate-400">Sin valor</span>
          ) : (
            display
          )}
        </span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ml-2 h-4 w-4 text-slate-300">
          <path d="M4 20h4l10-10-4-4L4 16v4z" />
        </svg>
      </button>
    );
  }

  return (
    <input
      id={inputId}
      ref={inputRef}
      type="text"
      value={draft}
      disabled={disabled}
      onChange={(event) => setDraft(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          setDraft(initialRef.current);
          setEditing(false);
        } else if (event.key === 'Enter') {
          event.preventDefault();
          (event.target as HTMLInputElement).blur();
        }
      }}
      onBlur={() => {
        const next = draft.trim();
        if (next === initialRef.current.trim()) {
          setEditing(false);
          return;
        }
        setEditing(false);
        void onCommit(next);
      }}
      className={clsx(
        'block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1',
        missing
          ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500'
          : 'border-slate-300 focus:border-primary focus:ring-primary',
      )}
    />
  );
}

function toDisplay(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
